/*
  Normalise the three stored result files into the {color, area, border} shape
  that wellExport.js turns into sheets.

  Each file has a different envelope, for historical reasons:

    growth/results.json        [{obj_id, frames:[{frame_idx, area_pixels, growth_rate}]}]
    morphology/results.json    [{obj_id, frames:[{frame_idx, circularity, ...}]}]   (same envelope)
    pigmentation/results.json  {frames: {frameIdx: {objId: {hue, saturation, ...}}}}  (inverted)

  Pigmentation being keyed frame-then-object rather than object-then-frame is the
  only real wrinkle: it has to be transposed before it can be read as a per-object
  time series.

  Area rows are merged from growth AND morphology: growth owns area/growth_rate,
  morphology owns equivalent_diameter_px, and Symbiomics want the media extent as
  both an area and a diameter (their E/J). Merging on frame_idx keeps one row per
  frame instead of two half-empty ones.
*/

/** growth | morphology -> {objId: [frames]} */
export function byObject(parsed) {
  const out = {};
  if (!Array.isArray(parsed)) return out;
  parsed.forEach((o) => {
    if (o && o.obj_id !== undefined) out[String(o.obj_id)] = o.frames || [];
  });
  return out;
}

/** pigmentation {frames:{frame:{obj:{...}}}} -> {objId: [{frame_idx, ...}]} */
export function pigmentByObject(parsed) {
  const out = {};
  const frames = parsed && parsed.frames;
  if (!frames || typeof frames !== 'object') return out;
  Object.entries(frames).forEach(([frameIdx, objs]) => {
    if (!objs || typeof objs !== 'object') return;
    Object.entries(objs).forEach(([objId, v]) => {
      (out[String(objId)] ||= []).push({ frame_idx: Number(frameIdx), ...(v || {}) });
    });
  });
  Object.values(out).forEach((rows) => rows.sort((a, b) => a.frame_idx - b.frame_idx));
  return out;
}

/** Merge growth + morphology frames on frame_idx into one area row per frame. */
export function mergeAreaFrames(growthFrames, morphFrames) {
  const byFrame = new Map();
  (growthFrames || []).forEach((f) => {
    byFrame.set(f.frame_idx, { ...byFrame.get(f.frame_idx), ...f });
  });
  (morphFrames || []).forEach((f) => {
    const prev = byFrame.get(f.frame_idx) || {};
    byFrame.set(f.frame_idx, {
      ...prev,
      frame_idx: f.frame_idx,
      // Only the fields the area sheet reads; the rest belongs to `border`.
      equivalent_diameter_px: f.equivalent_diameter_px,
      // morphology's area_px is the same measurement as growth's area_pixels;
      // prefer growth's, which every analysis has, and fall back to morphology's.
      area_pixels: prev.area_pixels !== undefined ? prev.area_pixels : f.area_px,
    });
  });
  return [...byFrame.values()].sort((a, b) => (a.frame_idx ?? 0) - (b.frame_idx ?? 0));
}

/**
 * Build the {color, area, border} bundle for ONE object of one analysis.
 * Any missing source simply yields an empty list, which wellExport renders as a
 * header-only sheet rather than dropping the characteristic.
 */
export function bundleFor(objId, { growth, pigmentation, morphology }) {
  const id = String(objId);
  const g = byObject(growth)[id] || [];
  const m = byObject(morphology)[id] || [];
  const p = pigmentByObject(pigmentation)[id] || [];
  return {
    color: p,
    area: mergeAreaFrames(g, m),
    border: m,
  };
}

/** Object ids present across any of the three sources, ascending. */
export function objectIds({ growth, pigmentation, morphology }) {
  const ids = new Set([
    ...Object.keys(byObject(growth)),
    ...Object.keys(byObject(morphology)),
    ...Object.keys(pigmentByObject(pigmentation)),
  ]);
  return [...ids].sort((a, b) => Number(a) - Number(b));
}

/**
 * Well identity from an analysis name — the operator's ground truth.
 *
 * The image FOLDER is not usable for this: an analysis is often segmented on a
 * neighbouring folder's frames when that framed the target well better (66 of
 * the 114 Jynx analyses), so the folder is off by a well more often than not.
 *
 * "3-B2_merged"  -> {plate:'3', well:'B2', side:null,    kind:'colony'}
 * "2-A1-media"   -> {plate:'2', well:'A1', side:null,    kind:'media'}
 * "B5_L"         -> {plate:null,well:'B5', side:'left',  kind:'colony'}
 */
export function identityFromName(name) {
  const m = /^(?:(\d)-)?([A-H]\d{1,2})(?:_(L|R))?(?:_merged)?(?:-media)?$/i.exec((name || '').trim());
  if (!m) return null;
  const side = m[3] ? (m[3].toUpperCase() === 'L' ? 'left' : 'right') : null;
  return {
    plate: m[1] || null,
    well: m[2].toUpperCase(),
    side,
    kind: /media/i.test(name) ? 'media' : 'colony',
  };
}

/** Frame index -> {elapsedH, iso}, from the persisted frame timestamps. */
export function timeResolver(secondsByFrame, isoByFrame) {
  return (fi) => {
    const s = secondsByFrame?.[fi] ?? secondsByFrame?.[String(fi)];
    return {
      elapsedH: typeof s === 'number' ? +(s / 3600).toFixed(4) : '',
      iso: isoByFrame?.[fi] ?? isoByFrame?.[String(fi)] ?? '',
    };
  };
}
